import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  OutlinedInput,
  Chip,
  Alert,
  List,
  ListItem,
  ListItemText
} from '@mui/material';
import apiClient from '../../services/apiClient';

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 8 + ITEM_PADDING_TOP,
      width: 400,
    },
  },
};

export default function UserList({ isLandmasterView = false }) {
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [gerichte, setGerichte] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    password: '',
    role_id: '',
    gericht_ids: [],
    is_active: true
  });
  const [editMode, setEditMode] = useState(false);
  const [editUserId, setEditUserId] = useState(null);
  const [error, setError] = useState(null);
  const [dialogError, setDialogError] = useState(null);

  const BUNDESLAND_NAMEN = {
    'BRD': 'Bundesrepublik Deutschland',
    'BWB': 'Baden-Württemberg',
    'FSB': 'Bayern',
    'BRB': 'Brandenburg',
    'HAM': 'Hamburg',
    'HES': 'Hessen',
    'MVP': 'Mecklenburg-Vorpommern',
    'NSB': 'Niedersachsen',
    'NRW': 'Nordrhein-Westfalen',
    'RPF': 'Rheinland-Pfalz',
    'SAA': 'Saarland',
    'FSS': 'Sachsen',
    'SAN': 'Sachsen-Anhalt',
    'SHS': 'Schleswig-Holstein',
    'FST': 'Thüringen'
  };

  const fetchUsers = useCallback(async () => {
    try {
      const endpoint = isLandmasterView ? '/users/?landmaster=true' : '/users/';
      const response = await apiClient.get(endpoint);
      setUsers(response.data);
      setError(null);
    } catch (error) {
      console.error('Fehler beim Laden der Benutzer:', error);
      setError('Fehler beim Laden der Benutzer');
    }
  }, [isLandmasterView]);

  const fetchRoles = useCallback(async () => {
    try {
      const response = await apiClient.get('/roles/');
      setRoles(response.data);
    } catch (error) {
      console.error('Fehler beim Laden der Rollen:', error);
      setError('Fehler beim Laden der Rollen');
    }
  }, []);

  const fetchGerichte = useCallback(async () => {
    try {
      const endpoint = isLandmasterView ? '/gerichte/?landmaster=true' : '/gerichte/';
      const response = await apiClient.get(endpoint);
      setGerichte(response.data);
      setError(null);
    } catch (error) {
      console.error('Fehler beim Laden der Gerichte:', error);
      setError('Fehler beim Laden der Gerichte');
    }
  }, [isLandmasterView]);

  useEffect(() => {
    fetchUsers();
    fetchRoles();
    fetchGerichte();
  }, [fetchUsers, fetchRoles, fetchGerichte]);

  const handleOpenDialog = (user = null) => {
    if (user) {
      setFormData({
        username: user.username,
        email: user.email,
        password: '',
        role_id: user.role_id,
        gericht_ids: user.gerichte ? user.gerichte.map(g => g.id) : [],
        is_active: user.is_active
      });
      setEditMode(true);
      setEditUserId(user.id);
    } else {
      setFormData({
        username: '',
        email: '',
        password: '',
        role_id: '',
        gericht_ids: [],
        is_active: true
      });
      setEditMode(false);
      setEditUserId(null);
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setFormData({
      username: '',
      email: '',
      password: '',
      role_id: '',
      gericht_ids: [],
      is_active: true
    });
    setEditMode(false);
    setEditUserId(null);
    setDialogError(null);
  };

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleGerichteChange = (event) => {
    const { value } = event.target;
    setFormData(prev => ({
      ...prev,
      gericht_ids: value
    }));
  };

  // Gruppiere Gerichte nach Bundesland und sortiere LSG zuerst
  const groupGerichteByBundesland = (gerichte) => {
    // Gruppiere Gerichte nach Bundesland
    const grouped = gerichte.reduce((acc, gericht) => {
      const bundeslandKey = gericht.bundesland;
      if (!acc[bundeslandKey]) {
        acc[bundeslandKey] = [];
      }
      acc[bundeslandKey].push(gericht);
      return acc;
    }, {});

    // Sortiere die Bundesländer alphabetisch nach ihren ausgeschriebenen Namen
    const sortedBundeslaender = Object.keys(grouped).sort((a, b) => {
      return (BUNDESLAND_NAMEN[a] || a).localeCompare(BUNDESLAND_NAMEN[b] || b);
    });

    // Erstelle ein neues Objekt mit sortierten Bundesländern
    const sortedGrouped = {};
    sortedBundeslaender.forEach(bundesland => {
      // Sortiere Gerichte innerhalb des Bundeslandes (LSG zuerst)
      grouped[bundesland].sort((a, b) => {
        if (a.instanz === 'LSG' && b.instanz !== 'LSG') return -1;
        if (a.instanz !== 'LSG' && b.instanz === 'LSG') return 1;
        return a.bezeichnung.localeCompare(b.bezeichnung);
      });
      sortedGrouped[bundesland] = grouped[bundesland];
    });

    return sortedGrouped;
  };

  const handleSubmit = async () => {
    try {
      setDialogError(null);
      if (editMode) {
        const updateData = { ...formData };
        if (!updateData.password) {
          delete updateData.password;
        }
        await apiClient.put(`/users/${editUserId}`, updateData);
      } else {
        await apiClient.post('/users/', formData);
      }
      handleCloseDialog();
      fetchUsers();
    } catch (error) {
      console.error('Fehler beim Speichern:', error);
      
      // Extrahiere die Fehlermeldung
      let errorMessage = "Ein Fehler ist aufgetreten";
      let errorDetails = ["Bitte versuchen Sie es später erneut"];

      if (error.response?.data?.detail) {
        const detail = error.response.data.detail;
        
        // Prüfe ob es ein ValidationError ist
        if (typeof detail === 'string' && detail.includes("Passwort entspricht nicht den Anforderungen")) {
          errorMessage = "Ungültiges Passwort";
          errorDetails = detail.split(": ")[1].split(", ");
        } else {
          errorMessage = typeof detail === 'object' ? detail.message || JSON.stringify(detail) : detail;
          errorDetails = Array.isArray(detail) 
            ? detail.map(err => typeof err === 'object' ? JSON.stringify(err) : err)
            : [typeof detail === 'object' ? JSON.stringify(detail) : detail];
        }
      }
      
      setDialogError({
        message: errorMessage,
        errors: errorDetails
      });
    }
  };

  const handleDelete = async (userId) => {
    if (window.confirm('Möchten Sie diesen Benutzer wirklich löschen?')) {
      try {
        await apiClient.delete(`/users/${userId}`);
        fetchUsers();
      } catch (error) {
        console.error('Fehler beim Löschen:', error);
      }
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="h5">
          {isLandmasterView ? 'Benutzerverwaltung (Landmaster)' : 'Benutzerverwaltung'}
        </Typography>
        <Button variant="contained" color="primary" onClick={() => handleOpenDialog()}>
          Neuer Benutzer
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Benutzername</TableCell>
              <TableCell>E-Mail</TableCell>
              <TableCell>Rolle</TableCell>
              <TableCell>Gerichte</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Aktionen</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {users.map((user) => (
              <TableRow key={user.id}>
                <TableCell>{user.username}</TableCell>
                <TableCell>{user.email}</TableCell>
                <TableCell>{roles.find(r => r.id === user.role_id)?.name}</TableCell>
                <TableCell>
                  {user.gerichte?.map(gericht => gericht.bezeichnung).join(', ') || 'Keine Gerichte zugewiesen'}
                </TableCell>
                <TableCell>{user.is_active ? 'Aktiv' : 'Inaktiv'}</TableCell>
                <TableCell>
                  <Button onClick={() => handleOpenDialog(user)}>Bearbeiten</Button>
                  <Button onClick={() => handleDelete(user.id)} color="error">
                    Löschen
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog 
        open={openDialog} 
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {editMode ? 'Benutzer bearbeiten' : 'Neuer Benutzer'}
          {isLandmasterView && ' (Landmaster)'}
        </DialogTitle>
        <DialogContent sx={{ minHeight: '500px' }}>
          {dialogError && (
            <>
              <Alert severity="error" sx={{ mb: 2 }}>
                {typeof dialogError.message === 'object' ? JSON.stringify(dialogError.message) : dialogError.message}
              </Alert>
              {dialogError.errors && dialogError.errors.length > 0 && (
                <List dense sx={{ mb: 2 }}>
                  {dialogError.errors.map((err, index) => (
                    <ListItem key={index}>
                      <ListItemText 
                        primary={typeof err === 'object' ? JSON.stringify(err) : err} 
                      />
                    </ListItem>
                  ))}
                </List>
              )}
            </>
          )}
          <Box sx={{ display: 'grid', gap: 2, pt: 2 }}>
            <TextField
              name="username"
              label="Benutzername"
              value={formData.username}
              onChange={handleInputChange}
              fullWidth
              required
            />
            <TextField
              name="email"
              label="E-Mail"
              type="email"
              value={formData.email}
              onChange={handleInputChange}
              fullWidth
              required
            />
            <TextField
              name="password"
              label={editMode ? 'Passwort (leer lassen für keine Änderung)' : 'Passwort'}
              type="password"
              value={formData.password}
              onChange={handleInputChange}
              fullWidth
              required={!editMode}
            />
            <FormControl fullWidth>
              <InputLabel>Rolle</InputLabel>
              <Select
                name="role_id"
                value={formData.role_id}
                onChange={handleInputChange}
                required
              >
                {roles.map((role) => (
                  <MenuItem key={role.id} value={role.id}>
                    {role.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel>Gerichte</InputLabel>
              <Select
                multiple
                value={formData.gericht_ids}
                onChange={handleGerichteChange}
                input={<OutlinedInput label="Gerichte" />}
                renderValue={(selected) => (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {selected.map((value) => (
                      <Chip
                        key={value}
                        label={gerichte.find(g => g.id === value)?.bezeichnung}
                      />
                    ))}
                  </Box>
                )}
                MenuProps={MenuProps}
              >
                {Object.entries(groupGerichteByBundesland(gerichte)).map(([bundesland, bundeslandGerichte]) => [
                  <MenuItem 
                    key={bundesland} 
                    disabled 
                    sx={{ 
                      fontWeight: 'bold',
                      backgroundColor: '#f5f5f5'
                    }}
                  >
                    {BUNDESLAND_NAMEN[bundesland] || bundesland}
                  </MenuItem>,
                  ...bundeslandGerichte.map((gericht) => (
                    <MenuItem 
                      key={gericht.id} 
                      value={gericht.id}
                      sx={{
                        pl: 4
                      }}
                    >
                      {gericht.bezeichnung}
                    </MenuItem>
                  ))
                ]).flat()}
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                name="is_active"
                value={formData.is_active}
                onChange={handleInputChange}
              >
                <MenuItem value={true}>Aktiv</MenuItem>
                <MenuItem value={false}>Inaktiv</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Abbrechen</Button>
          <Button onClick={handleSubmit} variant="contained" color="primary">
            {editMode ? 'Speichern' : 'Erstellen'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
} 